import { Field } from '@headlessui/react';
import {
  and,
  GroupLayout,
  isBooleanControl,
  isEnumControl,
  isGroup,
  isLayout,
  isMultiLineControl,
  JsonFormsRendererRegistryEntry,
  JsonSchema,
  Layout,
  optionIs,
  rankWith,
  RendererProps,
  scopeEndsWith,
  UISchemaElement,
  uiTypeIs,
} from '@jsonforms/core';
import {
  JsonFormsDispatch,
  withJsonFormsControlProps,
  withJsonFormsLayoutProps,
} from '@jsonforms/react';
import { vanillaRenderers } from '@jsonforms/vanilla-renderers';
import CT from '@marketplace-ai/tags';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FieldError, FormSection, Select, TextArea, TextInput, Toggle } from '~/components';
import { EnumCheckbox } from './EnumCheckbox';
import { MultiCheckbox } from './MultiCheckbox';

const TAGS = CT.genericJSONForm;

/**
 * Walk the react-hook-form `errors` tree to check whether a specific field
 * path currently has an error. Paths use dot notation (e.g. `foo.bar`).
 *
 * We need this because react-hook-form stores errors as a nested object
 * matching the form data shape, not as a flat map. The previous substring
 * check (`path.includes(Object.keys(errors)[0])`) produced false positives
 * and only considered the first error — causing unrelated fields to believe
 * they had errors and trigger scroll side-effects.
 */
function hasErrorAtPath(errors: unknown, path: string): boolean {
  const keys = path.split('.');
  let node: unknown = errors;

  for (const key of keys) {
    if (!node || typeof node !== 'object') {
      return false;
    }

    const record = node as Record<string, unknown>;

    // react-hook-form FieldError leaves have { type: string, message?, ref?, types? }.
    // Stop here so we don't traverse into those built-in properties and produce
    // false positives for form fields named "type", "message", etc.
    if (
      typeof record.type === 'string' ||
      'message' in record ||
      'ref' in record ||
      'types' in record
    ) {
      return false;
    }

    node = record[key];
  }

  return Boolean(node);
}

/**
 * Resolve the parent schema for a dot-path by walking `rootSchema.properties`.
 * Returns `undefined` if any segment is missing or the path resolves to the
 * root itself (no parent).
 */
function resolveParentSchema(
  rootSchema: JsonSchema,
  pathSegments: string[]
): JsonSchema | undefined {
  if (pathSegments.length === 0) return undefined;
  let node: JsonSchema | undefined = rootSchema;
  for (const seg of pathSegments) {
    const next: unknown = node?.properties?.[seg];
    if (!next || typeof next !== 'object') return undefined;
    node = next as JsonSchema;
  }
  return node;
}

/**
 * Pluck the slice of form data that corresponds to the given path segments.
 * Tolerates missing branches (returns `undefined`).
 */
function pluckData(data: unknown, pathSegments: string[]): unknown {
  let node: unknown = data;
  for (const seg of pathSegments) {
    if (!node || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[seg];
  }
  return node;
}

/**
 * Evaluate the subset of JSON Schema `if` that attestation/education/peer-
 * reference schemas actually use: `{ properties: { <key>: <constraint> },
 * required: [<key>, ...] }`, where `<constraint>` is either `{ const: <v> }`
 * or `{ anyOf: [{ const: <v> }, ...] }`. Returns true only when every required
 * key is present and every property constraint matches the instance.
 *
 * We do this targeted evaluation (rather than re-running AJV) so the render
 * path stays synchronous and side-effect-free. Extend `matchesConstraint`
 * if/when a new schema introduces other `if` keywords.
 */
function matchesConstraint(value: unknown, constraint: JsonSchema): boolean {
  if ('const' in constraint) {
    return JSON.stringify(value) === JSON.stringify(constraint.const);
  }
  const anyOf = (constraint as { anyOf?: JsonSchema[] }).anyOf;
  if (Array.isArray(anyOf)) {
    return anyOf.some((sub) => matchesConstraint(value, sub));
  }
  return true;
}

function evaluateIfCondition(ifSchema: JsonSchema, data: unknown): boolean {
  const ifProps = (ifSchema.properties ?? {}) as Record<string, JsonSchema>;
  const ifRequired = (ifSchema.required ?? []) as string[];
  const obj = (data ?? {}) as Record<string, unknown>;
  for (const req of ifRequired) {
    if (obj[req] === undefined) return false;
  }
  for (const [key, constraint] of Object.entries(ifProps)) {
    if (!matchesConstraint(obj[key], constraint)) return false;
  }
  return true;
}

/**
 * Authoritative `required` check for a field: consults the parent schema's
 * static `required` array *and* an `if/then/required` block if present,
 * matching how AJV validates the form. Keeps the asterisk rendering tied
 * to the actual data-layer constraint rather than to a UI `SHOW` rule.
 */
function isFieldRequiredBySchema(rootSchema: JsonSchema, path: string, formData: unknown): boolean {
  if (!path) return false;
  const segments = path.split('.');
  const fieldName = segments[segments.length - 1];
  const parentSegments = segments.slice(0, -1);
  const parentSchema = resolveParentSchema(rootSchema, parentSegments);
  if (!parentSchema) return false;

  if ((parentSchema.required ?? []).includes(fieldName)) return true;

  const thenRequired = ((parentSchema as { then?: JsonSchema }).then?.required ?? []) as string[];
  const ifSchema = (parentSchema as { if?: JsonSchema }).if;
  if (!ifSchema || !thenRequired.includes(fieldName)) return false;

  const parentData = pluckData(formData, parentSegments);
  return evaluateIfCondition(ifSchema, parentData);
}

/**
 * Scroll the target element into view when it has a validation error.
 *
 * Progressive enhancement: if the nearest scrollable ancestor doesn't implement
 * `scrollTo` (jsdom doesn't, for HTMLDivElement or HTMLFormElement), we fall
 * through to `window.scrollTo`. If window also lacks it (shouldn't happen),
 * we silently no-op rather than crashing the render.
 */
function scrollFieldIntoView(target: HTMLElement): void {
  const modalContainer = target.closest('.overflow-y-auto') as HTMLElement | null;
  if (modalContainer && typeof modalContainer.scrollTo === 'function') {
    modalContainer.scrollTo({
      top: target.offsetTop - modalContainer.offsetTop,
      left: 0,
      behavior: 'smooth',
    });
  } else if (typeof window.scrollTo === 'function') {
    // Fallback to window scroll if not in modal, or modal's scrollTo is
    // unavailable (e.g. jsdom in unit tests).
    window.scrollTo({
      top: target.offsetTop,
      left: 0,
      behavior: 'smooth',
    });
  }
}

/**
 * Renders checkbox property specifically for JSONForm.IO.
 * For Reference see https://jsonforms.io/
 */
export const CheckboxRenderer = withJsonFormsControlProps((props) => {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const {
    formState: { errors },
  } = useFormContext();
  const hasError = hasErrorAtPath(errors, props.path);
  // Scroll only when this field's error state transitions — never in the
  // render body, which would fire on every form state change (e.g. clicking
  // an unrelated checkbox) and pull the modal to wherever this field sits.
  useEffect(() => {
    if (hasError && scrollableRef.current) {
      scrollFieldIntoView(scrollableRef.current);
    }
  }, [hasError]);
  return (
    <Field>
      <div ref={scrollableRef}>
        <EnumCheckbox
          name={props.path}
          label={props.label}
          description={props.description}
          options={props.schema.enum || []}
          cyTag={TAGS.checkbox}
          required={props.required}
        />
      </div>
    </Field>
  );
});

/**
 * Renders multi-checkboxes property specifically for JSONForm.IO.
 * For Reference see https://jsonforms.io/
 */
export const MultiCheckboxRenderer = withJsonFormsControlProps((props) => {
  const scrollableRef = useRef<HTMLDivElement>(null);

  const {
    formState: { errors },
  } = useFormContext();
  const hasError = hasErrorAtPath(errors, props.path);
  useEffect(() => {
    if (hasError && scrollableRef.current) {
      scrollFieldIntoView(scrollableRef.current);
    }
  }, [hasError]);
  return (
    <Field>
      <div ref={scrollableRef}>
        <MultiCheckbox
          name={props.path}
          label={props.label}
          description={props.description}
          options={props.uischema.options?.enum || []}
          cyTag={TAGS.checkbox}
          required={props.required}
        />
      </div>
    </Field>
  );
});

/**
 * Renders Signature box property specifically for JSONForm.IO.
 * For Reference see https://jsonforms.io/
 */
export const SignatureRenderer = withJsonFormsControlProps((props) => {
  const scrollableRef = useRef<HTMLDivElement>(null);

  const {
    formState: { errors },
  } = useFormContext();
  const hasError = hasErrorAtPath(errors, props.path);
  useEffect(() => {
    // TODO: MCV-415 Determine Form Attention Strategy
    if (hasError && scrollableRef.current) {
      scrollFieldIntoView(scrollableRef.current);
    }
  }, [hasError]);
  return (
    <Field>
      <div ref={scrollableRef}>
        {/* The backend schema emits description="Please Sign" on the signature
            field. Passing that through to TextInput renders a gray FormHelper
            under the input that, together with this renderer's explicit
            FieldError below, reads as a "double message" under the signature
            (MCV-1151). The hardcoded label already explains what the user is
            acknowledging, so the description is redundant — omit it so the
            only text under the input is the error (when invalid) or nothing. */}
        <TextInput
          name={props.path}
          label={
            'By signing this, you acknowledge that you have truthfully answered all questions.'
          }
          cyTag={props.path + TAGS.signature}
          hideError
          required={props.required}
        />
      </div>
      {hasError && (
        <FieldError
          id={`${props.path}-error`}
          cyTag={props.path + '-error'}
          message={'You must sign to submit'}
          className="mt-2"
        />
      )}
    </Field>
  );
});

/**
 * Renders Boolean property specifically for JSONForm.IO
 * For Reference see https://jsonforms.io/
 */
export const ToggleRenderer = withJsonFormsControlProps((props) => {
  return (
    <Field className={'flex flex-col gap-2'}>
      <Toggle
        name={props.path}
        label={props.label}
        description={props.description}
        required={props.required}
      />
    </Field>
  );
});

/**
 * Renders enum property specifically for JSONForm.IO.
 * For Reference see https://jsonforms.io/
 */
export const SelectRenderer = withJsonFormsControlProps((props) => {
  return (
    <Field className={'flex flex-col gap-2 '}>
      <Select
        name={props.path}
        label={props.label}
        description={props.description}
        options={props.schema.enum || []}
        className="h-auto"
        required={props.required}
      />
    </Field>
  );
});

/**
 * Renders text property that has a "mutli" option set as true for JSONForm.IO.
 * For Reference see https://jsonforms.io/
 */
export const MultiLineRenderer = withJsonFormsControlProps((props) => {
  const uiSchema = props.uischema as UISchemaElement & { description?: string };
  const { clearErrors, getFieldState, formState } = useFormContext();
  const { isTouched, error } = getFieldState(props.path, formState);
  const hasError = Boolean(error);
  // Subscribe to the entire form so that `isFieldRequiredBySchema` re-runs
  // (and the asterisk state updates) as soon as a sibling controls the
  // parent's `if` condition (e.g. flipping the Yes/No Select).
  const formData = useWatch();

  // [MCV-1262] When a conditionally-shown "Please Explain" field becomes
  // visible (user picked "Yes" on the sibling Select), the Select's blur
  // triggers full-schema validation that immediately flags this now-required
  // empty field invalid — before the user has even seen it. Suppress the
  // error until the user has touched this field or attempted to submit.
  useEffect(() => {
    if (props.visible && !isTouched && !formState.isSubmitted && hasError) {
      clearErrors(props.path);
    }
  }, [props.visible, isTouched, formState.isSubmitted, hasError, props.path, clearErrors]);

  // [MCV-1262] Surface the asterisk when the schema — not the UI `SHOW`
  // rule — actually requires this field. `props.required` only reflects the
  // parent's static `required` array; AJV's if/then conditional required is
  // evaluated here against live form data so the marker tracks the same
  // logic the validator uses.
  const required =
    props.required || isFieldRequiredBySchema(props.rootSchema, props.path, formData);

  // [MCV-1151] The wrapped `TextArea` already renders its own inline
  // FieldError via react-hook-form's `useFormContext`. The previous code
  // rendered a *second* FieldError using `props.errors` from JSON Forms,
  // producing two identical red messages under every invalid question.
  // The `ajvResolver` bridges AJV validation into RHF errors, so the
  // RHF-driven inline error is sufficient.
  //
  // No top margin here: the enclosing layout renderer owns field-to-field
  // spacing (the shared 20px form rhythm), so an extra `mt-2` made a
  // conditionally revealed "Please Explain" sit 8px lower than every other
  // field in the same group.
  return (
    <Field className={props.visible ? 'flex flex-col gap-3' : 'hidden'}>
      <TextArea
        name={props.path}
        label={props.label}
        description={uiSchema.description}
        cyTag={props.path + TAGS.textBox}
        required={required}
      />
    </Field>
  );
});

/**
 * Renders "Group" layout types from JSONForm.io
 */
export const GroupRenderer = withJsonFormsLayoutProps((props) => {
  const { visible, schema, path, renderers } = props;
  const group = props.uischema as GroupLayout & { description?: string };
  if (!visible) {
    return null;
  }
  return (
    <div className={'mt-6 pt-4 border-t border-grey-800/30 first:border-t-0 first:pt-0'}>
      {group.label && <h2 className="text-lg font-semibold mb-1 text-left">{group.label}</h2>}
      {group.description && (
        <p className="text-sm text-content-secondary leading-relaxed mb-4">{group.description}</p>
      )}

      {/* Render children using JsonForms dispatch or recursive rendering.
          `gap-5` is the shared 20px field-to-field rhythm from `FormLayout`,
          so a schema-driven group stacks identically to a hand-authored form. */}
      <div className="flex flex-col gap-5">
        {group.elements.map((child, index) => (
          <Field key={`${path}-${index}`} className={'flex flex-col gap-3'}>
            <JsonFormsDispatch schema={schema} uischema={child} path={path} renderers={renderers} />
          </Field>
        ))}
      </div>
    </div>
  );
});

/**
 * Renders "Section" layout types from JSONForm.io
 */
export const SectionRenderer = withJsonFormsLayoutProps((props) => {
  interface SectionLayout extends Layout {
    label?: string;
    description?: string;
  }
  const { visible, schema, path, renderers } = props;
  const section = props.uischema as SectionLayout;
  if (!visible) {
    return null;
  }
  return (
    <div className={'mt-6 pt-4 border-t border-grey-800/30 first:border-t-0 first:pt-0'}>
      {section.label && <h2 className="text-lg font-extrabold mb-1">{section.label}</h2>}
      {section.description && (
        <p className="text-sm text-content-secondary leading-relaxed mb-4">{section.description}</p>
      )}

      {/* Render children using JsonForms dispatch or recursive rendering.
          `FormSection` replaces the hand-rolled per-child `Fieldset` grid: with
          `componentsPerLine={1}` every dispatched element still gets its own
          full-width row, but the stack now uses the shared 20px `gap-y-5`
          rhythm instead of a bespoke 16px one. */}
      <FormSection componentsPerLine={1}>
        {section.elements.map((child, index) => (
          <JsonFormsDispatch
            key={`${path}-${index}`}
            schema={schema}
            uischema={child}
            path={path}
            renderers={renderers}
          />
        ))}
      </FormSection>
    </div>
  );
});

const UnsupportedElementRenderer = (props: RendererProps) => {
  const { visible, uischema, schema, path, renderers } = props;
  if (!visible) return null;

  const elements = (uischema as Partial<Layout>).elements;
  if (Array.isArray(elements)) {
    return (
      // Same 20px stack rhythm as the Group/Section renderers, so an unknown
      // layout type degrades to the shared spacing rather than a tighter one.
      <div className="flex flex-col gap-5">
        {elements.map((child, index) => (
          <JsonFormsDispatch
            key={`${path}-${index}`}
            schema={schema}
            uischema={child}
            path={path}
            renderers={renderers ?? jsonFormRenderers}
          />
        ))}
      </div>
    );
  }

  console.warn('Unsupported JSON form UI schema element skipped', uischema);
  return null;
};

const booleanTester = rankWith(3, isBooleanControl);
export const multiCheckboxTester = rankWith(
  5,
  and(uiTypeIs('Control'), optionIs('customType', 'multiCheckbox'))
);

const enumTest = rankWith(3, isEnumControl);
const multilineTester = rankWith(3, isMultiLineControl);
const signatureTester = rankWith(
  10,
  and(scopeEndsWith('signature'), optionIs('customType', 'signature'))
);
const groupTester = rankWith(3, (uischema) => isLayout(uischema) && isGroup(uischema));
const sectionTester = rankWith(5, uiTypeIs('Section')); // Custom Tester
const unsupportedElementTester = () => 1;

export const renderers: JsonFormsRendererRegistryEntry[] = [
  ...vanillaRenderers,
  { tester: booleanTester, renderer: ToggleRenderer },
  { tester: enumTest, renderer: CheckboxRenderer },
  { tester: signatureTester, renderer: SignatureRenderer },
  { tester: multilineTester, renderer: MultiLineRenderer },
  { tester: sectionTester, renderer: SectionRenderer },
  { tester: groupTester, renderer: GroupRenderer },
  { tester: multiCheckboxTester, renderer: MultiCheckboxRenderer },
  { tester: unsupportedElementTester, renderer: UnsupportedElementRenderer },
];

const jsonFormRenderers = renderers;
